import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Button, Table } from "antd";
import classes from "./BannerTable.module.scss";
import imageTrash from "../../../../assets/images/trash.svg";
import { R_EDIT_BANNERS } from "../../../../constants/routes";

const BannerTable = ({ setModalDelete, bannersList, setSelectedBanner }) => {
  const history = useHistory();

  const bannersTableData = useMemo(() => {
    const sorted = (bannersList || [])
      .map((item) => {
        // separa o banner_id para pegar data e hora
        const parts = item.banner_id?.split("_");
        const date = parts?.[1];
        const time = parts?.[2];

        // junta data + hora para formar um datetime válido
        const createdAt = date && time ? `${date}T${time}` : null;
        return {
          ...item,
          created_date: createdAt, // usado para ordenação
          expires_in: item.expires_at || item.end_date || null,
        };
      })

      // ordena do mais recente para o mais antigo
      .sort((a, b) => {
        return new Date(b.created_date) - new Date(a.created_date);
      });

    // cria id de UI (sequencial)
    return sorted.map((item, index) => ({
      ...item,
      ui_id: index + 1,
    }));
  }, [bannersList]);

  //traduz o nome das colunas
  const getHeadTableNames = (name) => {
    const names = {
      ui_id: "Id",
      banner_name: "Banner",
      link_destination: "Exibido em",
      profile_can_view: "Profile",
      plan_name_can_view: "Plano",
      expires_in: "Expira em",
      status: "Status",
      actions: "Ações",
    };
    return names[name] || name;
  };

  // colunas aparecem na tabela
  const selectedColumns = [
    "id",
    "banner_name",
    "link_destination",
    "profile_can_view",
    "plan_name_can_view",
    "expires_in",
    "status",
    "actions",
  ];

  const ButtonEdit = ({ record }) => (
    <Button
      className={classes.EditBanner}
      onClick={() => {
        history.push(R_EDIT_BANNERS(record.banner_id), { banner: record });
      }}
    >
      Editar
    </Button>
  );

  const ButtonDelete = ({ record }) => (
    <Button
      className={classes.DeleteButton}
      danger
      onClick={() => {
        setModalDelete(true);
        setSelectedBanner(record);
      }}
    >
      <img src={imageTrash} alt="Ícone de lixeira" />
    </Button>
  );

  //Estilo para os tipos de painel
  const locationStyles = {
    Painel: {
      color: "#9150FB",
      background: "#F7F2FF",
      border: "1px solid #CDBCE9",
    },
    Atividade: {
      color: "#9D6619",
      background: "#fcf3c6",
      border: "1px solid #FFE35F",
    },
  };

  const STATUS = {
    ACTIVE: "ACTIVE",
    SOON: "SOON",
    EXPIRED: "EXPIRED",
  };

  //configuração visual de cada status
  const statusConfig = {
    [STATUS.ACTIVE]: {
      label: "Ativo",
      color: "#20B10D",
      background: "#DDF8DE",
      border: "1px solid #1CB846",
    },
    [STATUS.SOON]: {
      label: "Em breve",
      color: "#1890FF",
      background: " #F2F9FF",
      border: "1px solid #1890FF",
    },
    [STATUS.EXPIRED]: {
      label: "Expirado",
      color: "#9D1919",
      background: "#FCC",
      border: "1px solid #FF5F5F",
    },
  };

  //calcula status do banner com base nas datas
  const getStatus = (startDate, endDate) => {
    const now = new Date();

    const start =
      startDate && startDate !== "undefined" ? new Date(startDate) : null;

    const end = endDate && endDate !== "undefined" ? new Date(endDate) : null;

    // se ainda não começou
    if (start && now < start) {
      return STATUS.SOON;
    }

    // se já expirou
    if (end && now > end) {
      return STATUS.EXPIRED;
    }

    // se está dentro do período válido
    if (start && (!end || now <= end)) {
      return STATUS.ACTIVE;
    }

    return STATUS.ACTIVE;
  };

  // monta as colunas dinamicamente da tabela
  const columns = selectedColumns.map((key) => {
    // Extrai todos os valores da coluna atual
    const columnsValue = bannersList.map((row) => row[key]);
    // Remove valores duplicados manualmente
    const valueUniq = [];
    columnsValue.forEach((value) => {
      if (!valueUniq.includes(value)) {
        valueUniq.push(value);
      }
    });

    // Converte os valores únicos no formato esperado pelo filtro da tabela
    const filter = valueUniq.map((value) => ({
      text: value,
      value: value,
    }));

    return {
      dataIndex: key,
      title: getHeadTableNames(key),
      filters: filter,
      onFilter: (value, record) => record[key] === value,
      render: (value, record) => {
        if (key === "actions") {
          return (
            <div style={{ display: "flex", gap: "8px" }}>
              <ButtonEdit record={record} />
              <ButtonDelete record={record} />
            </div>
          );
        }

        if (key === "id") {
          return record?.ui_id;
        }

        // estilo da coluna link_destination
        if (key === "link_destination") {
          // traduz o valor do backend para um nome exibido na tela
          const screenMap = {
            panel: "Painel",
            activity_screen: "Atividade",
          };

          // pega o tipo da tela (Painel ou Atividade)
          const type = screenMap[record.displayed_screen];

          // aplica o estilo correspondente
          const style = locationStyles[type];

          return (
            <span
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                ...style,
              }}
            >
              {type}
            </span>
          );
        }

        if (key === "profile_can_view" || key === "plan_name_can_view") {
          let profiles = value;
          // Se vier como string, tenta transformar em array (JSON)
          if (typeof profiles === "string") {
            try {
              profiles = JSON.parse(profiles);
            } catch (error) {
              // Se não for JSON válido, transforma em array com 1 item só
              profiles = [profiles];
            }
          }
          // Junta os perfis em uma string separada por vírgula
          return profiles.join(", ");
        }

        // status do banner (calculado dinamicamente)
        if (key === "status") {
          const statusKey = getStatus(record?.start_date, record?.expires_in);
          const config = statusConfig[statusKey];

          return (
            <span
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                color: config.color,
                background: config.background,
                border: config.border,
                fontSize: "13px",
              }}
            >
              {config.label}
            </span>
          );
        }

        // caso não tenha data de expiração
        if (key === "expires_in") {
          const formattedDate = value
            ? new Date(value).toLocaleDateString("pt-br")
            : null;

          return value == null || value === "undefined" ? (
            <span>Indeterminado</span>
          ) : (
            formattedDate
          );
        }

        return value;
      },
    };
  });

  return (
    <Table
      className={classes.BannerTable}
      columns={columns}
      scroll={{ x: true }}
      dataSource={bannersTableData}
      rowKey={(record) => record.banner_id}
    />
  );
};

export default BannerTable;
